/**
 * FormUpload Block - Frontend script.
 * Handles file selection display, image preview, drag & drop, and limits validation.
 */

const __ = ( s ) =>
	typeof window !== 'undefined' && window.wp && window.wp.i18n
		? window.wp.i18n.__( s, 'brandy-blocks' )
		: s;

const sprintf = ( fmt, ...args ) =>
	typeof window !== 'undefined' && window.wp && window.wp.i18n
		? window.wp.i18n.sprintf( fmt, ...args )
		: args.reduce( ( s, a ) => s.replace( /%[sd]/, a ), fmt );

const _n = ( single, plural, n ) =>
	typeof window !== 'undefined' && window.wp && window.wp.i18n
		? window.wp.i18n._n( single, plural, n, 'brandy-blocks' )
		: n === 1
			? single
			: plural;

const escapeHtml = ( s ) =>
	String( s )
		.replace( /&/g, '&amp;' )
		.replace( /</g, '&lt;' )
		.replace( />/g, '&gt;' )
		.replace( /"/g, '&quot;' )
		.replace( /'/g, '&#39;' );

document.addEventListener( 'DOMContentLoaded', () => {
	document
		.querySelectorAll( '.wp-block-brandy-form-upload__dropzone' )
		.forEach( ( dropzone ) => {
			const input = dropzone.querySelector( 'input[type="file"]' );
			const placeholder = dropzone.querySelector(
				'.wp-block-brandy-form-upload__placeholder'
			);
			const errorEl = dropzone.parentElement?.querySelector(
				':scope > .wp-block-brandy-form-upload__error'
			);

			if ( ! input || ! placeholder ) return;

			const originalHtml = placeholder.innerHTML;
			const maxFiles = Math.max(
				1,
				parseInt( input.dataset.maxFiles, 10 ) || 1
			);
			const maxFileSizeMB =
				parseInt( input.dataset.maxFileSizeMb, 10 ) || 0;
			const maxFileSizeBytes = maxFileSizeMB > 0 ? maxFileSizeMB * 1024 * 1024 : 0;

			// Snapshot of input.files captured right before the native picker
			// opens. The browser replaces input.files atomically with the new
			// selection, so we need the prior state to merge against.
			let preClickSnapshot = [];

			/** Best-effort FileList assignment from an array of File objects. */
			const setInputFiles = ( arr ) => {
				try {
					const dt = new DataTransfer();
					arr.forEach( ( f ) => dt.items.add( f ) );
					input.files = dt.files;
				} catch {
					// DataTransfer unsupported — fall back to clearing.
					input.value = '';
				}
			};


			const showError = ( msg ) => {
				if ( errorEl ) {
					errorEl.textContent = msg;
					if ( typeof errorEl.scrollIntoView === 'function' ) {
						errorEl.scrollIntoView( {
							behavior: 'smooth',
							block: 'nearest',
						} );
					}
				}
			};

			const clearError = () => {
				if ( errorEl ) {
					errorEl.textContent = '';
				}
			};

			/**
			 * Partition an incoming batch against the limits and any baseline
			 * already present. Files that fit are returned in `accepted`;
			 * dropped ones are categorized so the caller can describe them.
			 */
			const partition = ( incoming, baseline ) => {
				const baselineKeys = new Set(
					baseline.map( fileKey )
				);
				const tooBig = [];
				const candidates = [];
				const seen = new Set( baselineKeys );

				Array.from( incoming ).forEach( ( f ) => {
					const key = fileKey( f );
					if ( seen.has( key ) ) return; // dedupe vs baseline + within batch
					seen.add( key );
					if ( maxFileSizeBytes > 0 && f.size > maxFileSizeBytes ) {
						tooBig.push( f );
						return;
					}
					candidates.push( f );
				} );

				const remainingSlots = Math.max( 0, maxFiles - baseline.length );
				const accepted = candidates.slice( 0, remainingSlots );
				const overCount = candidates.length - accepted.length;

				return { accepted, tooBig, overCount };
			};

			/**
			 * Build a single concise warning summarizing what was dropped.
			 * Returns '' when nothing notable happened.
			 */
			const buildWarning = ( incomingTotal, baseline, partResult ) => {
				const { accepted, tooBig, overCount } = partResult;
				const parts = [];

				if (
					baseline.length >= maxFiles &&
					incomingTotal > 0 &&
					accepted.length === 0 &&
					// Don't nag when nothing was over-size either — the summary
					// already shows "N of N"; a directive would be redundant.
					partResult.tooBig.length === 0
				) {
					parts.push(
						sprintf(
							__( 'Limit of %d files reached.' ),
							maxFiles
						)
					);
				} else if ( overCount > 0 ) {
					parts.push(
						sprintf(
							_n(
								'Added %1$d file; %2$d skipped (limit of %3$d).',
								'Added %1$d files; %2$d skipped (limit of %3$d).',
								accepted.length
							),
							accepted.length,
							overCount,
							maxFiles
						)
					);
				}

				if ( tooBig.length > 0 ) {
					const names = tooBig
						.slice( 0, 3 )
						.map( ( f ) => `"${ f.name }"` )
						.join( ', ' );
					const extra =
						tooBig.length > 3
							? sprintf( __( ' (+%d more)' ), tooBig.length - 3 )
							: '';
					parts.push(
						sprintf(
							_n(
								'Skipped %1$d file over %2$d MB: %3$s%4$s',
								'Skipped %1$d files over %2$d MB: %3$s%4$s',
								tooBig.length
							),
							tooBig.length,
							maxFileSizeMB,
							names,
							extra
						)
					);
				}

				return parts.join( ' ' );
			};

			const fileKey = ( f ) =>
				`${ f.name }|${ f.size }|${ f.lastModified || 0 }`;

			/** Human-readable byte size. */
			const formatSize = ( bytes ) => {
				if ( ! bytes ) return '0 B';
				const units = [ 'B', 'KB', 'MB', 'GB' ];
				const i = Math.min(
					units.length - 1,
					Math.floor( Math.log( bytes ) / Math.log( 1024 ) )
				);
				const n = bytes / Math.pow( 1024, i );
				return ( i === 0 ? n : n.toFixed( n < 10 ? 1 : 0 ) ) + ' ' + units[ i ];
			};

			/**
			 * Remove a single file from the input by index and re-render preview.
			 * Falls back to clearing all files if DataTransfer is unsupported.
			 */
			const removeFileAt = ( index ) => {
				const remaining = Array.from( input.files ).filter(
					( _, i ) => i !== index
				);
				setInputFiles( remaining );
				if ( input.files.length === 0 ) {
					placeholder.innerHTML = originalHtml;
					dropzone.classList.remove( 'has-file' );
					clearError();
				} else {
					showPreview( input.files );
					clearError();
				}
			};

			/**
			 * Render every selected file as a compact pill with thumbnail,
			 * filename, size and per-item remove button.
			 */
			const showPreview = ( files ) => {
				const list = Array.from( files );
				const totalBytes = list.reduce( ( a, f ) => a + f.size, 0 );

				const itemsHtml = list
					.map( ( f, i ) => {
						const safeName = escapeHtml( f.name );
						const sizeStr = escapeHtml( formatSize( f.size ) );
						return `
							<div class="wp-block-brandy-form-upload__preview-item" data-index="${ i }">
								<span class="wp-block-brandy-form-upload__preview-thumb" data-thumb-index="${ i }">
									<span class="wp-block-brandy-form-upload__preview-icon-small">📎</span>
								</span>
								<span class="wp-block-brandy-form-upload__preview-item-meta">
									<span class="wp-block-brandy-form-upload__preview-item-name" title="${ safeName }">${ safeName }</span>
									<span class="wp-block-brandy-form-upload__preview-item-size">${ sizeStr }</span>
								</span>
								<button type="button" class="wp-block-brandy-form-upload__preview-item-remove" data-remove-index="${ i }" aria-label="${ escapeHtml( __( 'Remove file' ) ) }: ${ safeName }">&#x2715;</button>
							</div>
						`;
					} )
					.join( '' );

				const atLimit = list.length >= maxFiles;
				const countLabel = sprintf(
					__( '%1$d of %2$d files · %3$s' ),
					list.length,
					maxFiles,
					formatSize( totalBytes )
				);
				const addHint = atLimit
					? ''
					: ` · <span class="wp-block-brandy-form-upload__preview-add-hint">${ escapeHtml(
							__( 'click to add more' )
					  ) }</span>`;

				placeholder.innerHTML = `
					<div class="wp-block-brandy-form-upload__preview-list">
						${ itemsHtml }
					</div>
					<div class="wp-block-brandy-form-upload__preview-summary">${ escapeHtml( countLabel ) }${ addHint }</div>
				`;

				dropzone.classList.add( 'has-file' );

				// Async-load image thumbnails into their slots.
				list.forEach( ( f, i ) => {
					if ( ! f.type.startsWith( 'image/' ) ) return;
					const slot = placeholder.querySelector(
						`[data-thumb-index="${ i }"]`
					);
					if ( ! slot ) return;
					const reader = new FileReader();
					reader.onload = ( e ) => {
						slot.innerHTML = `<img src="${ escapeHtml( e.target.result ) }" alt="${ escapeHtml( f.name ) }" />`;
					};
					reader.readAsDataURL( f );
				} );

				// Per-item remove handlers (event delegation would also work).
				placeholder
					.querySelectorAll(
						'.wp-block-brandy-form-upload__preview-item-remove'
					)
					.forEach( ( btn ) => {
						btn.addEventListener( 'click', ( e ) => {
							e.stopPropagation();
							const idx = parseInt(
								btn.dataset.removeIndex,
								10
							);
							if ( Number.isFinite( idx ) ) {
								removeFileAt( idx );
							}
						} );
					} );
			};

			/**
			 * Clear UI state. Pass keepError=true when the reset is triggered
			 * by a validation failure so the user can see the rejection notice.
			 */
			const resetInput = ( keepError = false ) => {
				input.value = '';
				placeholder.innerHTML = originalHtml;
				dropzone.classList.remove( 'has-file' );
				if ( ! keepError ) {
					clearError();
				}
			};

			/**
			 * Merge an incoming batch (from drop or picker) with the existing
			 * selection. Partial-accept: files that fit are added; files
			 * exceeding the count cap or per-file size are skipped and the
			 * user sees a single concise warning describing what was dropped.
			 */
			const mergeAndPreview = ( incoming, baseline ) => {
				const incomingTotal = Array.from( incoming ).length;
				const part = partition( incoming, baseline );
				const merged = [ ...baseline, ...part.accepted ];

				if ( part.accepted.length > 0 ) {
					setInputFiles( merged );
					showPreview( input.files );
				} else {
					// Nothing accepted — preserve baseline as-is.
					setInputFiles( baseline );
					if ( baseline.length > 0 ) {
						showPreview( input.files );
					} else {
						placeholder.innerHTML = originalHtml;
						dropzone.classList.remove( 'has-file' );
					}
				}

				const warning = buildWarning( incomingTotal, baseline, part );
				if ( warning ) {
					showError( warning );
				} else {
					clearError();
				}
			};

			// Capture the prior selection before the native picker replaces it.
			input.addEventListener( 'click', () => {
				preClickSnapshot = Array.from( input.files );
			} );

			input.addEventListener( 'change', () => {
				if ( input.files && input.files.length > 0 ) {
					mergeAndPreview( input.files, preClickSnapshot );
				} else if ( preClickSnapshot.length > 0 ) {
					// Browser cleared selection but we had files — restore.
					setInputFiles( preClickSnapshot );
				} else {
					resetInput();
				}
				preClickSnapshot = [];
			} );

			dropzone.addEventListener( 'dragover', ( e ) => {
				e.preventDefault();
				dropzone.classList.add( 'is-dragging' );
			} );

			dropzone.addEventListener( 'dragleave', ( e ) => {
				if ( ! dropzone.contains( e.relatedTarget ) ) {
					dropzone.classList.remove( 'is-dragging' );
				}
			} );

			dropzone.addEventListener( 'drop', ( e ) => {
				e.preventDefault();
				dropzone.classList.remove( 'is-dragging' );
				const files = e.dataTransfer.files;
				if ( files.length ) {
					mergeAndPreview( files, Array.from( input.files ) );
				}
			} );
		} );
} );
